import { useState } from "react";
import { Fragment } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { TemplateCard } from "@/components/confluence/templates/template-card";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { Template } from "@/content/templates/template.types.ts";

import { tabbedSectionHeading } from "./template-benefits-section.tsx";

interface TemplateRelatedTemplatesSectionProps {
    template: Template;
}

export const TemplateRelatedTemplatesSection = ({
    template
}: TemplateRelatedTemplatesSectionProps) => {
    const intl = useIntl();
    const previousLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselPreviousLabel
    );
    const nextLabel = intl.formatMessage(
        ConfluenceTemplateMessages.carouselNextLabel
    );

    const relatedTemplatesStyles = css({
        "display": "none",
        "@media(min-width: 1130px)": {
            display: "flex",
            gap: "32px"
        }
    });

    const relatedTemplatesMobileStyles = css({
        "alignItems": "center",
        "display": "flex",
        "flexWrap": "wrap",
        "gap": "32px",
        "@media(min-width: 1130px)": {
            display: "none"
        }
    });

    const relatedSectionStyles = css({
        "marginBlockStart": 120,
        "marginInlineStart": "auto",
        "marginInlineEnd": "auto",
        "@media(max-width: 769px)": {
            marginBlockStart: "64px"
        }
    });

    const buttonLeft = css({
        "backgroundColor": "#ffffff",
        "display": "block",
        "boxSizing": "border-box",
        "insetInlineStart": 0,
        "insetBlockStart": "50%",
        "width": 32,
        "height": 32,
        "border": "none",
        "boxShadow": "1px 1px 6px 0 rgba(0,0,0,.2)",
        "transform": "translateY(-40%)",
        "borderRadius": "50%",
        "outline": "none",
        "cursor": "pointer",
        "color": "#0052cc",
        "lineHeight": 28,
        "zIndex": 1,
        "align-self": "center"
    });

    const buttonRight = css({
        "backgroundColor": "#ffffff",
        "boxSizing": "border-box",
        "display": "block",
        "insetInlineEnd": 0,
        "insetBlockStart": "50%",
        "width": 32,
        "height": 32,
        "border": "none",
        "boxShadow": "1px 1px 6px 0 rgba(0,0,0,.2)",
        "transform": "translateY(-40%)",
        "borderRadius": "50%",
        "outline": "none",
        "cursor": "pointer",
        "color": "#0052cc",
        "lineHeight": 28,
        "zIndex": 1,
        "align-self": "center"
    });

    const buttonLeftArrow = css({
        transform: "rotate(-45deg)",
        width: 12,
        height: 12,
        display: "inline-block",
        lineHeight: 0,
        position: "absolute",
        marginBlockStart: -6,
        marginInlineStart: -4,
        boxSizing: "border-box",
        borderBlockStart: "3px solid",
        borderInlineStart: "3px solid",
        fontSize: 16
    });

    const buttonRightArrow = css({
        transform: "rotate(135deg)",
        width: 12,
        height: 12,
        display: "inline-block",
        lineHeight: 0,
        position: "absolute",
        marginBlockStart: -6,
        marginInlineStart: -8,
        boxSizing: "border-box",
        borderBlockStart: "3px solid",
        borderInlineStart: "3px solid",
        fontSize: 16
    });

    const LeftArrow = ({ onClick }: { onClick?: () => void }) => (
        <button css={buttonLeft} onClick={onClick} aria-label={previousLabel}>
            <span css={buttonLeftArrow} />
        </button>
    );
    const RightArrow = ({ onClick }: { onClick?: () => void }) => (
        <button css={buttonRight} onClick={onClick} aria-label={nextLabel}>
            <span css={buttonRightArrow} />
        </button>
    );

    const [currentIndex, setCurrentIndex] = useState(0);

    const goToNext = () => {
        setCurrentIndex(
            (prevIndex) => (prevIndex + 1) % relatedTemplates.length
        );
    };

    const goToPrev = () => {
        setCurrentIndex(
            (prevIndex) =>
                (prevIndex - 1 + relatedTemplates.length) %
                relatedTemplates.length
        );
    };

    const relatedTemplates = template.relatedTemplates || [];
    const hasRelatedTemplates = relatedTemplates.length > 0;

    return (
        <section css={relatedSectionStyles}>
            <h2 css={tabbedSectionHeading}>
                <FormattedMessage
                    {...ConfluenceTemplateMessages.relatedTemplatesTitle}
                />
            </h2>
            {hasRelatedTemplates && (
                <>
                    <div css={relatedTemplatesStyles}>
                        {currentIndex !== 0 && <LeftArrow onClick={goToPrev} />}
                        {relatedTemplates
                            .slice(currentIndex, currentIndex + 2)
                            .map((relatedTemplate) => (
                                <Fragment key={relatedTemplate.templateId}>
                                    <TemplateCard template={relatedTemplate} />
                                </Fragment>
                            ))}
                        {currentIndex + 2 < relatedTemplates.length && (
                            <RightArrow onClick={goToNext} />
                        )}
                    </div>
                    <div css={relatedTemplatesMobileStyles}>
                        {relatedTemplates.map((relatedTemplate) => (
                            <Fragment key={relatedTemplate.templateId}>
                                <TemplateCard template={relatedTemplate} />
                            </Fragment>
                        ))}
                    </div>
                </>
            )}
        </section>
    );
};
